/**
 * Send a `sso_required` refusal to the workspace's identity provider.
 *
 * 🔴 Kept deliberately OUT of `api.ts`'s status handling and given its own
 * tests, because it is the one thing in this phase that adds a 403 which
 * NAVIGATES. `api.ts` was rewritten this month to stop 403 meaning "logged
 * out" — 401 answers "who are you?", 403 answers "may you do this?" — and the
 * bug class was a status answering two questions at once.
 *
 * This is still the second question. The session is valid; it simply was not
 * established by the identity provider this workspace insists on. So the user
 * is sent to that provider, and **the session is left exactly as it was**. It
 * still works everywhere else, including their personal workspace, which is
 * the whole point of `require_sso` being a property of the workspace rather
 * than of the person.
 */

/** The bits of a Response this needs — so a test can hand it a plain object. */
export interface SsoResponseLike {
  status: number;
  headers: Pick<Headers, "get">;
}

export interface SsoRedirectEnv {
  pathname: string;
  /** Where to come back to after the IdP round trip. */
  returnTo?: string;
  navigate: (url: string) => void;
}

/** Paths where a redirect would create a loop rather than progress. */
const NO_REDIRECT_PREFIXES = ["/sso/", "/login"];

function sameOrigin(url: string): boolean {
  try {
    // A relative path resolves against our own origin, so it always passes —
    // which is why the backend now sends one. An absolute URL still has to
    // match, because this value becomes a navigation.
    return new URL(url, window.location.origin).origin === window.location.origin;
  } catch {
    return false;
  }
}

export function handleSsoRequired(
  response: SsoResponseLike,
  body: { code?: string } | null,
  env: SsoRedirectEnv,
): boolean {
  if (response.status !== 403 || body?.code !== "sso_required") return false;

  // Already mid-SSO: a misconfigured tenant would otherwise bounce between our
  // callback and the IdP forever, and a loop is harder to diagnose than an error.
  if (NO_REDIRECT_PREFIXES.some((p) => env.pathname.startsWith(p))) return false;

  const start = response.headers.get("X-SSO-Start");
  // No URL means no redirect. Guessing one would be inventing a destination.
  if (!start || !sameOrigin(start)) return false;

  // Carry the page this refusal interrupted, so the round trip returns the
  // user to what they were doing rather than to a generic dashboard.
  const target = new URL(start, window.location.origin);
  if (env.returnTo && !target.searchParams.has("return_to")) {
    target.searchParams.set("return_to", env.returnTo);
  }
  env.navigate(start.startsWith("/") ? target.pathname + target.search : target.toString());
  return true;
}
